import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAccountOpeningStep } from "../../redux/accountOpening";
import { useFormik } from "formik";
import { RegularSavingsDocsPayload } from "../../models/IEnums.model";
import "react-html5-camera-photo/build/css/index.css";
import { initialRegularDocs } from "../../utils/constants";
import "youverify-web-sdk";
import DocUpload from "../DocUpload";
import UploadSignatureCard from "../UploadSignatureCard";
import SectionLabel from "../SectionLabel";
import CustomInputField from "../CustomInputField/CustomInputField";
import RadioBtn from "../../assets/icons/RadioBtn";
import apiHooks from "../../customHooks/apis";
import {
  extractValuesFromObj,
  handleNumericInput,
  handleNumericPaste,
  isEmptyObj,
  returnInputErrorMessage,
} from "../../utils/helpers";
import FormHelperText from "../FormHelperText";
import SignaturePad from "../SignaturePad";
import CustomModal from "../Modal/CustomModal";
import { regularDocsValidationSchema } from "../../utils/validationSchemas";
import useUserValidated from "../../customHooks/useUserValidated";
import ImageUploadAndLivePhoto from "../ImageUploadAndLivePhoto";
import { string } from "yup";

function UploadDocumentSavingsDialog(props: any) {
  const dispatch = useDispatch();
  const {
    useGetMeansOfId,
    useUploadFile,
    useUploadSavingsDocs,
    useUserDetails,
  } = apiHooks;
  const { idTypes } = useGetMeansOfId();
  const { handleUpdateUserDetails, userDetails } = useUserDetails();
  const { handleUploadFile, uploadingFile } = useUploadFile();
  const { handleUploadRegularDocs, submittingRegularDocs } =
    useUploadSavingsDocs();
  const { isValidated, isValid } = useUserValidated();
  const formik = useFormik<RegularSavingsDocsPayload>({
    initialValues: initialRegularDocs,
    validationSchema: regularDocsValidationSchema.shape({
      imageUrl: isValid
        ? string().notRequired()
        : string().required("Image is required"),
    }),
    onSubmit: handleUploadRegularDocs,
    validateOnBlur: true,
    validateOnMount: true,
  });
  const [docUrl, setDocUrl] = useState<{ drawn: string; uploaded: string }>({
    drawn: "",
    uploaded: "",
  });
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState(false);

  const goToStep = (step: any) => {
    handleUpdateUserDetails(formik.values);
    if (step === "update-detail") {
      localStorage.setItem("previous-screen", "document-upload");
    }
    dispatch(setAccountOpeningStep(step));
  };

  const handleClickSave = (
    postedFilebase64: string,
    documentName: string,
    filetype: string,
    objectUrl: string,
  ) => {
    setDocUrl({ ...docUrl, drawn: objectUrl });
    setIsOpenModal(!isOpenModal);
    handleUploadFile(
      {
        postedFilebase64,
        documentName,
        filetype,
      },
      handleToggleModal,
    );
  };

  const handleToggleModal = (url: string) => {
    if (url) return formik.setFieldValue("signatureUrl", url);
    setIsOpenModal(!isOpenModal);
  };

  useEffect(() => {
    const accTypeFromStorage = localStorage.getItem("altFinanceAccountType");
    const glCodeFromStorage = localStorage.getItem("altFinanceGlCode");
    formik.setValues({
      ...initialRegularDocs,
      ...extractValuesFromObj(initialRegularDocs, userDetails),
      accountTypecode: glCodeFromStorage || "",
      accountTypeName: accTypeFromStorage || "",
      imageUrl: localStorage.getItem("userImage") || "",
      idCardNumber: userDetails.nin || "",
    });
  }, [idTypes, userDetails]);

  useEffect(() => {
    const signatureUrl = docUrl.uploaded;
    if (signatureUrl) {
      formik.setValues({
        ...formik.values,
        signatureUrl,
      });
    }
  }, [docUrl.uploaded]);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className=" card-form pl-1 pr-1 ">
          <div className="card-body">
            <h4 className="card-title text-center pl-3 mt-5">
              Document upload
            </h4>
            <p className="text-center">
              <small>
                The file Format should be JPEG, PNG or PDF (max size:2mb)
              </small>
            </p>
            <FormHelperText />
            <div className="d-flex flex-lg-row flex-column align-items-end w-100 gap-3">
              <div className="d-flex flex-column flex-lg-row align-items-end w-lg-50 w-100 gap-2">
                <UploadSignatureCard
                  uploading={uploadingFile}
                  imageUrl={docUrl.drawn}
                  required
                  handleRemoveFile={(drawn) => setDocUrl({ ...docUrl, drawn })}
                  width="w-lg-50 w-100"
                  show={!docUrl.uploaded}
                  label="Draw/Upload Signature"
                  onClickCard={() => handleToggleModal("")}
                  disabled={isUploading}
                />
                <div className="w-lg-50 w-100">
                  <DocUpload
                    required
                    show={!docUrl.drawn}
                    getFileUrl={(_, uploaded) =>
                      setDocUrl({ ...docUrl, uploaded })
                    }
                    setIsUploading={setIsUploading}
                    disabled={uploadingFile}
                  />
                </div>
              </div>
              <ImageUploadAndLivePhoto
                getFileUrl={(url) => formik.setFieldValue("imageUrl", url)}
              />
            </div>
            <div className="my-4">
              <DocUpload
                name="utilityBill"
                required
                show
                label="Upload utility bill (not older than 3 months)"
                getFileUrl={formik.setFieldValue}
              />
            </div>
            <SectionLabel label="Means of Identification Document" />

            <div className="d-flex flex-column gap-3">
              <div className="d-flex flex-column flex-lg-row align-items-end w-100 gap-3">
                <CustomInputField
                  label="Identity Card Type"
                  value="National Identity Card"
                  required
                  disabled
                />
                <CustomInputField
                  required
                  label="National Identity Number (NIN)"
                  placeholder="Enter ID Number"
                  name="idCardNumber"
                  value={formik.values.idCardNumber}
                  onChange={formik.handleChange}
                  onKeyDown={(e) =>
                    handleNumericInput(e, formik.values.identityCardType)
                  }
                  onPaste={(e) =>
                    handleNumericPaste(e, formik.values.identityCardType)
                  }
                  onBlur={formik.handleBlur}
                  errorMessage={
                    returnInputErrorMessage<typeof formik.initialValues>(
                      formik,
                      "idCardNumber",
                    ) as string
                  }
                  disabled
                />
              </div>
              <DocUpload
                name="frontPageIdUrl"
                required
                show
                label="Front page of ID"
                bottomText="The file format should be JPEG, PNG or PDF (max size: 2mb)"
                getFileUrl={formik.setFieldValue}
                errorMessage={returnInputErrorMessage<RegularSavingsDocsPayload>(
                  formik,
                  "frontPageIdUrl",
                )}
              />
              <div className="d-flex flex-column gap-2 w-lg-50 w-100">
                <small className="fw-medium">
                  Would you like to have a Debit Card (Verve)
                </small>
                <div className="w-100 d-flex justify-content-between">
                  <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                    <RadioBtn
                      color="#181717"
                      checked={!!formik.values.debitCardValueAddedService}
                      onClickCheck={() =>
                        formik.setFieldValue("debitCardValueAddedService", true)
                      }
                    />
                    Yes
                  </span>
                  <span className="w-50 d-flex gap-2 fs-6 fw-medium">
                    <RadioBtn
                      color="#181717"
                      checked={!formik.values.debitCardValueAddedService}
                      onClickCheck={() =>
                        formik.setFieldValue(
                          "debitCardValueAddedService",
                          false,
                        )
                      }
                    />
                    No
                  </span>
                </div>
              </div>
            </div>

            {/* --------------- */}
          </div>
          <div className="d-flex justify-content-end mt-2">
            <div className="mb-3 mr-4" style={{ marginRight: "20px" }}>
              <button
                type="button"
                onClick={() => {
                  goToStep("update-detail");
                }}
                className="btn btn-outline-gold mr-3 btn-filled-white"
              >
                Previous
              </button>
            </div>
            {submittingRegularDocs ? (
              <div className="spinner-border text-gold" role="status">
                <span className="sr-only"></span>
              </div>
            ) : (
              <button
                type="submit"
                className="btn btn-filled-red"
                disabled={!isEmptyObj(formik.errors) || !isValidated}
              >
                Proceed
              </button>
            )}
          </div>
        </div>
      </form>
      <CustomModal
        show={isOpenModal}
        onClickClose={() => handleToggleModal("")}
      >
        <SignaturePad onClickSave={handleClickSave} />
      </CustomModal>
    </div>
  );
}

export default UploadDocumentSavingsDialog;
